// ============================================================
// LanguageContext.jsx — Globaler Sprachkontext
// Verwendung:
//   1. <LanguageProvider> in App.js um alles wickeln
//   2. const { lang, setLang, t } = useLanguage(); in jedem Component
// ============================================================

import { createContext, useContext, useState } from "react";
import { translations, detectLanguage, LANGUAGES } from "./translations";

// ── Context ──────────────────────────────────────────────────────────────────
const LanguageContext = createContext(null);

// ── Provider ─────────────────────────────────────────────────────────────────
export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    // 1. Aus localStorage (User-Präferenz)
    const saved = localStorage.getItem("aiv_lang");
    if (saved && ["de", "en", "fr", "it"].includes(saved)) return saved;
    // 2. Browser-Sprache
    return detectLanguage();
  });

  const handleSetLang = (code) => {
    setLang(code);
    localStorage.setItem("aiv_lang", code);
  };

  // Übersetzungsfunktion direkt im Context
  const resolveNode = (key) => {
    const parts = key.split(".");
    let node = translations;
    for (const p of parts) {
      node = node?.[p];
      if (!node) return null;
    }
    return node;
  };

  // options.count === 1 tries "<key>_one" first (i18next-style singular
  // suffix), falling back to the base key if no singular variant exists.
  const translate = (key, options) => {
    let node = null;
    if (options && options.count === 1) {
      node = resolveNode(`${key}_one`);
    }
    if (!node) node = resolveNode(key);
    if (!node) return key;
    return node[lang] ?? node["de"] ?? node["en"] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang: handleSetLang, t: translate, LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside <LanguageProvider>");
  return ctx;
}
